/**
 * Detects the single most common way a hand-drawn parcel becomes unusable: a
 * stray vertex dropped within a few metres of its neighbour (a double-click, or
 * a fumbled drag), which makes the ring double back and cross itself.
 *
 * Pineywoods Park p3 is the motivating case — 180.74 acres rejected outright
 * because vertex 10 sits 12 m from vertex 11 while every other edge in the ring
 * runs 80–775 m. The polygon is otherwise exactly what the user drew.
 *
 * This proposes a repair; it never applies one. The caller decides, because
 * editing a study polygon invalidates downstream reports.
 *
 * DELIBERATELY NARROW. It offers a fix only when removing one short-spur vertex
 * resolves EVERY self-intersection, so an ambiguous or badly tangled polygon is
 * left for a human rather than silently "cleaned" into a different shape. Area
 * is not preserved by definition — it is preserved in practice because a spur
 * this short encloses a negligible sliver — so the caller is told the delta and
 * can refuse it.
 */
import { findRingSelfIntersections, MAX_AUTHORITY_BOUNDARY_VERTICES_PER_RING } from './generationImpact/boundaryAuthority';

export interface LatLngPoint { lat: number; lng: number }

export interface StrayVertexRepair {
  /** Index into the OPEN ring (no repeated closing vertex). */
  index: number;
  /** Length of the spur edge that pulled this vertex out of line. */
  spurMeters: number;
  /** Shortest edge length that survives the repair — context for the caller. */
  nextShortestEdgeMeters: number;
  selfIntersectionsBefore: number;
  /** The repaired ring, closed the same way the input was. */
  repaired: LatLngPoint[];
  /** Signed change in planar ring area, as a fraction. Expect ~0. */
  areaChangeFraction: number;
}

const DEFAULT_MAX_SPUR_METERS = 20;
const EARTH_METERS_PER_DEGREE = 111_320;

/** Local planar approximation — accurate enough at the scale a spur lives at. */
function metersBetween(a: LatLngPoint, b: LatLngPoint): number {
  const dy = (b.lat - a.lat) * EARTH_METERS_PER_DEGREE;
  const dx = (b.lng - a.lng) * EARTH_METERS_PER_DEGREE * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dx, dy);
}

function planarRingArea(ring: readonly LatLngPoint[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    sum += (b.lng - a.lng) * (b.lat + a.lat);
  }
  return sum / 2;
}

function isClosed(polygon: readonly LatLngPoint[]): boolean {
  if (polygon.length < 2) return false;
  const first = polygon[0]!;
  const last = polygon[polygon.length - 1]!;
  return first.lat === last.lat && first.lng === last.lng;
}

/** Strip a repeated closing vertex, if present. */
export function toOpenRing(polygon: readonly LatLngPoint[]): LatLngPoint[] {
  const ring = polygon.slice();
  if (isClosed(ring)) ring.pop();
  return ring;
}

/** Vertices the authority would keep: consecutive repeats (cyclically) collapse, as its cleanRing does. */
function distinctVertexCount(ring: readonly LatLngPoint[]): number {
  let count = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    if (a.lat !== b.lat || a.lng !== b.lng) count += 1;
  }
  return Math.max(count, 1);
}

/**
 * Propose removing one stray vertex, or return null when that would not
 * unambiguously fix the polygon.
 */
export function detectStrayVertex(
  polygon: readonly LatLngPoint[],
  options: { maxSpurMeters?: number } = {},
): StrayVertexRepair | null {
  const maxSpur = options.maxSpurMeters ?? DEFAULT_MAX_SPUR_METERS;
  const ring = toOpenRing(polygon);
  if (ring.length < 4) return null; // removing a vertex cannot leave a polygon
  // Removing one vertex drops at most two distinct vertices (its neighbours may then repeat), so a ring
  // with more than MAX+2 can never become a usable boundary: the authority rejects it on vertex count.
  // Returning first also keeps the quadratic scans below off rings no repair could help.
  if (distinctVertexCount(ring) > MAX_AUTHORITY_BOUNDARY_VERTICES_PER_RING + 2) return null;

  const before = findRingSelfIntersections(ring);
  if (before.length === 0) return null; // nothing to repair

  const edgeLength = (i: number) => metersBetween(ring[i]!, ring[(i + 1) % ring.length]!);

  // Candidate vertices: those touching an edge shorter than the spur threshold.
  // Both endpoints are candidates — either one may be the accidental click.
  const candidates = new Set<number>();
  for (let i = 0; i < ring.length; i += 1) {
    if (edgeLength(i) <= maxSpur) {
      candidates.add(i);
      candidates.add((i + 1) % ring.length);
    }
  }
  if (candidates.size === 0) return null;

  // Removing vertex v replaces only edges v-1 and v, and adjacency is by shared vertex, so a crossing
  // [e1, e2] survives unless v is one of e1, e1+1, e2, e2+1. A candidate outside that set for ANY
  // crossing can never yield a simple ring; dropping it here skips only rescans that would fail.
  for (const [e1, e2] of before) {
    const touching = new Set([e1, (e1 + 1) % ring.length, e2, (e2 + 1) % ring.length]);
    for (const candidate of [...candidates]) if (!touching.has(candidate)) candidates.delete(candidate);
    if (candidates.size === 0) return null;
  }

  const areaBefore = Math.abs(planarRingArea(ring));

  for (const index of [...candidates].sort((a, b) => a - b)) {
    const repairedRing = ring.filter((_, i) => i !== index);
    if (repairedRing.length < 3) continue;
    if (findRingSelfIntersections(repairedRing).length > 0) continue;

    const areaAfter = Math.abs(planarRingArea(repairedRing));
    const spurMeters = Math.min(
      metersBetween(ring[index]!, ring[(index + 1) % ring.length]!),
      metersBetween(ring[(index - 1 + ring.length) % ring.length]!, ring[index]!),
    );
    const nextShortest = Math.min(
      ...repairedRing.map((_, i) => metersBetween(repairedRing[i]!, repairedRing[(i + 1) % repairedRing.length]!)),
    );
    const repaired = isClosed(polygon) ? [...repairedRing, repairedRing[0]!] : repairedRing;

    return {
      index,
      spurMeters,
      nextShortestEdgeMeters: nextShortest,
      selfIntersectionsBefore: before.length,
      repaired,
      areaChangeFraction: areaBefore === 0 ? 0 : (areaAfter - areaBefore) / areaBefore,
    };
  }

  // A tangle that one short vertex does not explain. Leave it to a human.
  return null;
}
